import { accessApi } from '@/modules/iam/api/access-api'
import { groupsApi } from '@/modules/iam/api/groups-api'
import { usersApi } from '@/modules/iam/api/users-api'
import { skipToken, useMutation, useQuery } from '@tanstack/react-query'

import { ApiError, ignoreAlreadyGone } from '@/lib/api'
import { apiErrorMessage } from '@/lib/api-error-message'
import { type AccessGrant, type AccessSummary } from '@/modules/iam/api/access-api'
import { accessSummaryKeys, iamInvalidates, assistantEffectMeta } from '../../api/query-keys'

export type AccessSummaryState =
  | { status: 'loading' }
  | { status: 'ready'; summary: AccessSummary }
  | { status: 'error'; message: string; retry: () => void }

// Bindings are a plain read at any level of the tree (the Hierarchy tab shows
// them everywhere); user-management-entity status governs management, not this read.
export function useAccessSummary(entityId: string | null): AccessSummaryState {
  const query = useQuery({
    queryKey: accessSummaryKeys.summary(entityId ?? ''),
    queryFn:
      entityId === null ? skipToken : () => accessApi.getAccessSummary(entityId),
    retry: (count, error) => !(error instanceof ApiError) && count < 2,
    staleTime: 30_000,
    meta: assistantEffectMeta,
  })

  if (query.isSuccess) return { status: 'ready', summary: query.data }
  if (query.isError) {
    return {
      status: 'error',
      message: apiErrorMessage(
        query.error,
        'Couldn’t load access for this entity. Try again.',
      ),
      retry: () => void query.refetch(),
    }
  }
  return { status: 'loading' }
}

export function useRemoveGrant(entityId: string) {
  return useMutation({
    mutationFn: (grant: AccessGrant) =>
      ignoreAlreadyGone(() =>
        grant.subjectKind === 'machine'
          ? usersApi.revokeServiceAccountRole(entityId, grant.subjectId, grant.roleId)
          : grant.subjectKind === 'user'
            ? usersApi.revokeUserRole(entityId, grant.subjectId, grant.roleId)
          : groupsApi.revokeGroupRole(entityId, grant.subjectId, grant.roleId),
      ),
    meta: { invalidates: iamInvalidates() },
  })
}
